/**
 * MapViewer - Visor de Infraestructuras Integrado - V06.4
 * Integra MapContainer + FilterPanel + InfrastructureLayer + EditMode + AddressSearch
 * 
 * PTEL - Sistema de Normalización de Coordenadas
 * 
 * V14-V16: Soporte para edición de coordenadas
 * V17: Búsqueda de direcciones con CartoCiudad
 */

import React, { memo, useCallback, useRef, useEffect, useMemo, useState } from 'react';
import { Marker, Popup, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import type { Map as LeafletMap, LeafletMouseEvent } from 'leaflet';
import type { NormalizedInfrastructure } from '../../../types/infrastructure';
import { MapContainer, type MapContainerRef, type MapPoint } from '../MapContainer';
import { LayerControl } from '../LayerControl';
import { type TileProviderId } from '../../../services/tiles';
import { FilterPanel } from '../FilterPanel';
import { InfrastructureLayer, getBounds } from '../InfrastructureLayer';
import { 
  EditPanel, 
  EditableInfrastructureLayer, 
  useEditMode,
  type PendingEdit,
} from '../editing';
// V17: AddressSearch integrado
import { AddressSearch } from '../search';
import { ZoomControl } from '../controls/ZoomControl';
import { MunicipalitySearch } from '../controls/MunicipalitySearch';
import '../controls/controls.css';
import { MapExport } from '../MapExport';
import { MapLegend } from '../MapLegend';
import { MapAttributionNotes } from '../MapAttributionNotes';
import { CatastroWMSLayer } from '../CatastroWMSLayer';
// VECMUN-c: término propio, vecinos y sus nombres (solo si el consumidor pasa `terminoIne5`).
import { TerminoMunicipalCapa } from '../TerminoMunicipal';
import type { MapExportMetadata } from '../../../services/export/MapExportService';
import { useMapViewer } from './useMapViewer';
import { toWGS84 } from '../../../lib/leaflet-crs';
import './MapViewer.css';

// =============================================================================
// TIPOS
// =============================================================================

/**
 * P2RED-S1-5b · Marcador candidato efímero de geocodificación asistida.
 * Posición en WGS84 crudo — la reproyección EPSG:25830→WGS84 la hace el
 * consumidor (Step2Page → `pointToWGS84`), espejo del contrato de `onLienzoClic`.
 */
export interface CandidatoMapa {
  /** Identificador estable del candidato (despacho del «Aplicar»). */
  id: string;
  lat: number;
  lng: number;
  /** Texto principal del popup (dirección o nombre del candidato). */
  etiqueta: string;
  /** Fuente de geocodificación (LOCAL_DERA, CDAU…), informativa. */
  fuente?: string;
  /** Mejor candidato — se realza el icono. */
  destacado?: boolean;
}

export interface MapViewerProps {
  /** Datos de infraestructuras a visualizar */
  data: NormalizedInfrastructure[];
  /** Código INE del municipio */
  codigoINE?: string;
  /**
   * VECMUN-c: INE de 5 dígitos del término a enseñar (el propio con su contorno y nombre, y los
   * términos vecinos con los suyos). Solo si viene y no está vacía se monta la capa. Es una prop
   * distinta de `codigoINE` a propósito: esta fija la clave de los filtros guardados
   * (`ptel-filters-<INE>`), el nombre de exportación y el aria-label, y no deben cambiar.
   */
  terminoIne5?: string;
  /** Centro inicial del mapa (WGS84) */
  initialCenter?: MapPoint;
  /** Zoom inicial (1-18) */
  initialZoom?: number;
  /** Mostrar panel de filtros */
  showFilters?: boolean;
  /** Posición del panel de filtros */
  filterPosition?: 'left' | 'right';
  /** Panel de filtros colapsable */
  filtersCollapsible?: boolean;
  /** Panel de filtros colapsado por defecto */
  filtersDefaultCollapsed?: boolean;
  /** Persistir estado de filtros en sessionStorage */
  persistFilters?: boolean;
  /** Altura del contenedor */
  height?: string;
  /** Habilitar clustering automático */
  enableClustering?: boolean;
  /** Umbral para activar clustering */
  clusterThreshold?: number;
  /** Callback cuando se selecciona una infraestructura */
  onSelect?: (infra: NormalizedInfrastructure | null) => void;
  /** Callback cuando cambian los datos filtrados */
  onFilteredDataChange?: (data: NormalizedInfrastructure[]) => void;
  /** Callback cuando el mapa está listo */
  onMapReady?: (map: LeafletMap) => void;
  /** Clases CSS adicionales */
  className?: string;
  /** ID para accesibilidad */
  id?: string;
  
  // V14-V16: Props de edición
  /** Habilitar modo edición de coordenadas */
  enableEditing?: boolean;
  /** Posición del panel de edición */
  editPanelPosition?: 'left' | 'right';
  /** Callback cuando se confirman ediciones */
  onEditsConfirmed?: (edits: PendingEdit[], updatedData: NormalizedInfrastructure[]) => void;
  /** Callback cuando se descarta una edición */
  onEditDiscarded?: () => void;
  /** Callback cuando cambia una coordenada (antes de confirmar) */
  onCoordinateChange?: (edit: PendingEdit) => void;
  
  // V18: Selección bidireccional
  /** ID de infraestructura seleccionada externamente */
  selectedInfraId?: string;
  
  // F8.06: Exportar + Leyenda
  /** Mostrar toolbar de exportación PNG/PDF */
  showExport?: boolean;
  /** Mostrar leyenda de infraestructuras */
  showLegend?: boolean;
  /** Mostrar panel de atribución de fuentes de datos */
  showAttribution?: boolean;
  /** Mostrar capa WMS de Catastro (parcelas + edificios, zoom ≥ 14) */
  showCatastroWMS?: boolean;
  /** Opacidad de la capa WMS de Catastro (0-1, default 0.6) */
  catastroWMSOpacity?: number;
  /** Nombre base del archivo de exportación (sin extensión) */
  exportFilename?: string;

  // ADV-02: Control de capas base
  /** Mostrar selector de capa base (PNOA/IGN/OSM) */
  showLayerControl?: boolean;
  /** Proveedor de tiles por defecto */
  defaultTileProvider?: TileProviderId;
  /** Posición del control de capas */
  layerControlPosition?: 'topleft' | 'topright' | 'bottomleft' | 'bottomright';
  /** Callback cuando cambia el proveedor de tiles */
  onTileProviderChange?: (providerId: TileProviderId) => void;

  // V17: Búsqueda de direcciones CartoCiudad
  /** Mostrar buscador de direcciones CartoCiudad (opt-in) */
  showAddressSearch?: boolean;
  /** Posición del buscador de direcciones */
  addressSearchPosition?: 'top-left' | 'top-right' | 'top-center';
  /** Municipio para filtrar búsquedas de CartoCiudad */
  municipio?: string;
  /** Provincia para filtrar búsquedas de CartoCiudad */
  provincia?: string;

  // A3x-4: click-to-set (fijar coordenada por clic en el lienzo no-edición)
  /**
   * Callback al hacer clic en el lienzo en la rama no-edición. Emite el punto en
   * WGS84 crudo (`{lat,lng}`); el consumidor (useSeleccionMapaTabla) reproyecta a
   * EPSG:25830. Solo se cablea cuando se proporciona; el clic en un marcador no
   * propaga al clic de mapa (Leaflet), de modo que solo el vacío del lienzo lo dispara.
   */
  onLienzoClic?: (latlng: MapPoint) => void;
  /**
   * A3x-5 · Callback al mover el cursor sobre el lienzo. Emite el punto en WGS84
   * crudo (`{lat,lng}`) o `null` al salir del mapa (`mouseout`); el consumidor
   * (Step2Page → `pointToUTM30`) reproyecta a EPSG:25830 para la `StatusBar`.
   * `mousemove` es de alta frecuencia → el emisor lo limita con `rAF` (un disparo
   * por frame). Solo se cablea cuando se proporciona.
   */
  onCursorMover?: (latlng: MapPoint | null) => void;

  // P2RED-S1-5b: capa efímera de candidatos de geocodificación asistida
  /**
   * Candidatos a plotear como marcadores efímeros en la rama no-edición. El
   * ciclo de vida lo gobierna el consumidor: aparecen con la selección y se
   * limpian al cambiarla (DOP-S1-5B-LIMPIEZA=a).
   */
  candidatos?: CandidatoMapa[];
  /**
   * Callback del botón «Aplicar» del popup de un candidato
   * (DOP-S1-5B-INTERACCION=a: confirmación explícita — el clic en el marcador
   * solo abre el popup). Si no se proporciona (p. ej. rol viewer), el popup se
   * muestra sin botón.
   */
  onAplicarCandidato?: (candidatoId: string) => void;
}

// Centro por defecto: Andalucía
const DEFAULT_CENTER: MapPoint = { lat: 37.3891, lng: -5.9845 };
const DEFAULT_ZOOM = 8;

/**
 * A3x-4 · Captura de clic en el lienzo para click-to-set (rama no-edición).
 * Espeja el patrón de `EditableInfrastructureLayer` (hijo con `useMapEvents`),
 * pero emite el punto en WGS84 crudo: la reproyección a EPSG:25830 la hace el
 * consumidor (`useSeleccionMapaTabla` → `pointToUTM30`). Devuelve `null` (sin
 * render). Leaflet no propaga el clic de marcador al clic de mapa, así que solo
 * el vacío del lienzo dispara el callback.
 */
const LienzoClicCapture = memo<{ onClic: (latlng: MapPoint) => void }>(
  function LienzoClicCapture({ onClic }) {
    useMapEvents({
      click: (event: LeafletMouseEvent) => {
        onClic({ lat: event.latlng.lat, lng: event.latlng.lng });
      },
    });
    return null;
  },
);

/**
 * A3x-5 · Captura del cursor sobre el lienzo para la `StatusBar` (coords en vivo).
 * Espeja `LienzoClicCapture` pero escucha `mousemove`/`mouseout` y emite WGS84
 * crudo (reproyecta el consumidor). `mousemove` dispara muchas veces por segundo,
 * así que se limita con `requestAnimationFrame`: se guarda el último punto y se
 * emite a lo sumo una vez por frame. `mouseout` cancela el frame pendiente y emite
 * `null` (oculta el bloque cursor). Devuelve `null` (sin render).
 */
const CursorCoordCapture = memo<{ onMover: (latlng: MapPoint | null) => void }>(
  function CursorCoordCapture({ onMover }) {
    const frameRef = useRef<number | null>(null);
    const ultimoRef = useRef<MapPoint | null>(null);
    useMapEvents({
      mousemove: (event: LeafletMouseEvent) => {
        ultimoRef.current = { lat: event.latlng.lat, lng: event.latlng.lng };
        if (frameRef.current != null) return;
        frameRef.current = requestAnimationFrame(() => {
          frameRef.current = null;
          if (ultimoRef.current != null) onMover(ultimoRef.current);
        });
      },
      mouseout: () => {
        if (frameRef.current != null) {
          cancelAnimationFrame(frameRef.current);
          frameRef.current = null;
        }
        ultimoRef.current = null;
        onMover(null);
      },
    });
    useEffect(
      () => () => {
        if (frameRef.current != null) cancelAnimationFrame(frameRef.current);
      },
      [],
    );
    return null;
  },
);

// P2RED-S1-5b · Iconos del marcador candidato (DS v2, sin glow — regla dura 4).
// Hex de `design-tokens` v2: teal #2dd4bf (primary) · overlay #1e1e1c. Se
// construyen a nivel de módulo (cacheados; los divIcon son inmutables).
const candidatoIcon = L.divIcon({
  className: 'ptel-candidato-marker',
  html: '<div style="width:14px;height:14px;border-radius:50%;background:#1e1e1c;border:2.5px solid #2dd4bf;box-sizing:border-box;"></div>',
  iconSize: [14, 14],
  iconAnchor: [7, 7],
  popupAnchor: [0, -9],
});
const candidatoIconDestacado = L.divIcon({
  className: 'ptel-candidato-marker ptel-candidato-marker--destacado',
  html: '<div style="width:18px;height:18px;border-radius:50%;background:#2dd4bf;border:2.5px solid #1e1e1c;box-sizing:border-box;"></div>',
  iconSize: [18, 18],
  iconAnchor: [9, 9],
  popupAnchor: [0, -11],
});

const candidatoAplicarBtnStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  padding: '4px 10px',
  background: '#2dd4bf',
  color: '#0F1A18',
  border: 'none',
  borderRadius: 6,
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: 600,
  fontFamily: 'inherit',
};

/**
 * P2RED-S1-5b · Capa efímera de candidatos de geocodificación asistida (rama
 * no-edición). Espejo del patrón hijo de `LienzoClicCapture`: Leaflet no
 * propaga el clic de marcador al clic de mapa, así que los candidatos no
 * colisionan ni con la selección ni con el modo «Ubicar» armado. Aplicar exige
 * confirmación explícita en el popup (DOP-S1-5B-INTERACCION=a).
 */
const CandidatosLayer = memo<{
  candidatos: CandidatoMapa[];
  onAplicar?: ((candidatoId: string) => void) | undefined;
}>(function CandidatosLayer({ candidatos, onAplicar }) {
  return (
    <>
      {candidatos.map((c) => (
        <Marker
          key={c.id}
          position={[c.lat, c.lng]}
          icon={c.destacado === true ? candidatoIconDestacado : candidatoIcon}
        >
          <Popup>
            <div style={{ minWidth: 180, fontFamily: "'Space Grotesk', sans-serif" }}>
              <strong style={{ display: 'block', marginBottom: 4 }}>{c.etiqueta}</strong>
              <span style={{ display: 'block', fontSize: 11, opacity: 0.75, marginBottom: 6 }}>
                {c.fuente != null ? `Fuente: ${c.fuente}` : 'Candidato'}
                {c.destacado === true ? ' · mejor candidato' : ''}
              </span>
              {onAplicar != null && (
                <button
                  type="button"
                  data-testid={`candidato-aplicar-${c.id}`}
                  onClick={() => onAplicar(c.id)}
                  style={candidatoAplicarBtnStyle}
                >
                  Aplicar esta coordenada
                </button>
              )}
            </div>
          </Popup>
        </Marker>
      ))}
    </>
  );
});

// =============================================================================
// COMPONENTE
// =============================================================================

export const MapViewer = memo<MapViewerProps>(function MapViewer({
  data,
  codigoINE,
  terminoIne5,
  initialCenter,
  initialZoom = DEFAULT_ZOOM,
  showFilters = true,
  filterPosition = 'left',
  filtersCollapsible = true,
  filtersDefaultCollapsed = false,
  persistFilters = true,
  height = '100vh',
  enableClustering = true,
  clusterThreshold = 50,
  onSelect,
  onFilteredDataChange,
  onMapReady,
  className = '',
  id,
  // V14-V16: Props de edición
  enableEditing = false,
  editPanelPosition = 'right',
  onEditsConfirmed,
  onEditDiscarded,
  onCoordinateChange,
  // V18: Selección bidireccional
  selectedInfraId,
  // A3x-4: click-to-set
  onLienzoClic,
  onCursorMover,
  // P2RED-S1-5b: candidatos de geocodificación asistida
  candidatos,
  onAplicarCandidato,
  // F8.06: Exportar + Leyenda
  showExport = true,
  showLegend = true,
  showAttribution = true,
  exportFilename,
  // ADV-02: Control de capas base
  showLayerControl = true,
  defaultTileProvider = 'ign-base',
  layerControlPosition = 'topright',
  onTileProviderChange,
  // V17: Búsqueda de direcciones CartoCiudad
  showAddressSearch = false,
  addressSearchPosition = 'top-right',
  municipio,
  provincia,
  // F7.03: Capa WMS Catastro
  showCatastroWMS = false,
  catastroWMSOpacity = 0.6,
}) {
  const mapRef = useRef<MapContainerRef>(null);
  const mapExportRef = useRef<HTMLDivElement>(null);
  
  // Estado para diálogo de confirmación al salir con cambios
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  
  // ADV-02: Estado del proveedor de tiles activo
  const [activeProvider, setActiveProvider] = useState<TileProviderId>(defaultTileProvider);

  // Hook de integración (V06.1)
  const {
    filteredData,
    stats,
    filters,
    setFilters,
    selection,
  } = useMapViewer({
    data,
    codigoINE,
    persist: persistFilters,
    storageKey: codigoINE ? `ptel-filters-${codigoINE}` : 'ptel-filters',
  });

  // V14-V16: Hook de edición
  const editMode = useEditMode({
    onConfirm: useCallback((edits: PendingEdit[]) => {
      // Aplicar cambios a los datos
      const updatedData = data.map(infra => {
        const edit = edits.find(e => e.infrastructureId === infra.id);
        if (edit) {
          return {
            ...infra,
            x: edit.newCoords.x,
            y: edit.newCoords.y,
            // Marcar como editado manualmente
            source: `${infra.source || 'original'} → manual`,
            metadata: {
              ...infra.metadata,
              manuallyEdited: true,
              editedAt: new Date().toISOString(),
              previousCoords: edit.originalCoords,
            },
          };
        }
        return infra;
      });
      
      onEditsConfirmed?.(edits, updatedData);
    }, [data, onEditsConfirmed]),
    onDiscard: onEditDiscarded,
    onCoordinateChange,
  });

  // F8.06: Construir metadatos de exportación
  const exportMetadata = useMemo((): MapExportMetadata => ({
    codigoINE: codigoINE ?? '',
    nombreMunicipio: '',
    provincia: '',
    totalInfraestructuras: filteredData.length,
  }), [codigoINE, filteredData.length]);

  const exportBaseFilename = useMemo(() => {
    if (exportFilename) return exportFilename;
    return codigoINE ? `mapa-ptel-${codigoINE}` : 'mapa-ptel';
  }, [exportFilename, codigoINE]);

  // Notificar cambios en datos filtrados
  useEffect(() => {
    onFilteredDataChange?.(filteredData);
  }, [filteredData, onFilteredDataChange]);

  // V18: Auto-centrado flyTo al seleccionar infraestructura desde tabla
  useEffect(() => {
    if (!selectedInfraId || !mapRef.current) return;
    
    const infra = filteredData.find(i => i.id === selectedInfraId);
    if (!infra) return;
    
    const map = mapRef.current.getMap();
    if (!map) return;
    
    const [lat, lng] = toWGS84(infra.x, infra.y);
    map.flyTo([lat, lng], 16, { duration: 0.5 });
  }, [selectedInfraId, filteredData]);

  // Handler de selección (recibe infraestructura, puede pasar null externamente)
  const handleSelect = useCallback((infra: NormalizedInfrastructure) => {
    selection.select(infra);
    onSelect?.(infra);
  }, [selection, onSelect]);

  // Handler de mapa listo - fitBounds automático a los datos
  const handleMapReady = useCallback((map: LeafletMap) => {
    // FitBounds automático si hay datos y no se especificó centro inicial
    if (!initialCenter && filteredData.length > 0) {
      const bounds = getBounds(filteredData);
      if (bounds) {
        map.fitBounds(
          [[bounds.minLat, bounds.minLng], [bounds.maxLat, bounds.maxLng]],
          { padding: [50, 50], maxZoom: 16 }
        );
      }
    }
    onMapReady?.(map);
  }, [onMapReady, initialCenter, filteredData]);

  // Calcular centro desde datos si no se proporciona
  const center = useMemo((): MapPoint => {
    if (initialCenter) return initialCenter;
    if (filteredData.length === 0) return DEFAULT_CENTER;

    // Centro de los datos filtrados
    const bounds = getBounds(filteredData);
    if (!bounds) return DEFAULT_CENTER;
    
    return {
      lat: (bounds.maxLat + bounds.minLat) / 2,
      lng: (bounds.maxLng + bounds.minLng) / 2,
    };
  }, [initialCenter, filteredData]);

  // Decidir si usar clustering (desactivado en modo edición)
  const shouldCluster = enableClustering && 
    filteredData.length > clusterThreshold && 
    !editMode.state.isActive;

  // Aria label dinámico
  const ariaLabel = useMemo(() => {
    const municipio = codigoINE ? ` de ${codigoINE}` : '';
    const editStatus = editMode.state.isActive ? ' (Modo edición activo)' : '';
    return `Mapa de infraestructuras${municipio}. Mostrando ${stats.filtered} de ${stats.total} elementos.${editStatus}`;
  }, [codigoINE, stats.filtered, stats.total, editMode.state.isActive]);

  // V14-V16: Handlers de edición
  const handleToggleEdit = useCallback(() => {
    if (editMode.state.isActive && editMode.state.hasUnsavedChanges) {
      setShowExitConfirm(true);
    } else {
      editMode.toggleEditMode();
    }
  }, [editMode]);

  const handleConfirmEdits = useCallback(() => {
    editMode.confirmAllEdits();
  }, [editMode]);

  const handleDiscardEdits = useCallback(() => {
    editMode.discardAllEdits();
    setShowExitConfirm(false);
  }, [editMode]);

  const handleUndoEdit = useCallback((infrastructureId: string) => {
    editMode.undoEdit(infrastructureId);
  }, [editMode]);

  const handleSelectForEdit = useCallback((infrastructureId: string) => {
    editMode.selectForEdit(infrastructureId);
  }, [editMode]);

  const handleDragEnd = useCallback((
    infra: NormalizedInfrastructure, 
    newX: number, 
    newY: number
  ) => {
    editMode.registerEdit(infra, newX, newY, 'drag');
  }, [editMode]);

  const handleMapClick = useCallback((x: number, y: number) => {
    // Si hay una infraestructura seleccionada, actualizar sus coordenadas
    if (editMode.state.selectedId) {
      const selectedInfra = data.find(i => i.id === editMode.state.selectedId);
      if (selectedInfra) {
        editMode.registerEdit(selectedInfra, x, y, 'click');
      }
    }
  }, [editMode, data]);

  const handleInfraSelect = useCallback((infra: NormalizedInfrastructure) => {
    if (editMode.state.isActive) {
      editMode.selectForEdit(infra.id);
    } else {
      handleSelect(infra);
    }
  }, [editMode, handleSelect]);

  // Handler para confirmar salida con cambios
  const handleConfirmExit = useCallback(() => {
    editMode.discardAllEdits();
    editMode.toggleEditMode();
    setShowExitConfirm(false);
  }, [editMode]);

  const handleCancelExit = useCallback(() => {
    setShowExitConfirm(false);
  }, []);

  // ADV-02: Handler para cambio de proveedor de tiles
  const handleProviderChange = useCallback((providerId: TileProviderId) => {
    setActiveProvider(providerId);
    onTileProviderChange?.(providerId);
  }, [onTileProviderChange]);

  // V17: Handler para navegación desde AddressSearch (UTM30 → WGS84 → flyTo)
  const handleAddressNavigate = useCallback((x: number, y: number) => {
    if (!mapRef.current) return;
    const map = mapRef.current.getMap();
    if (!map) return;
    const [lat, lng] = toWGS84(x, y);
    map.flyTo([lat, lng], 17, { duration: 0.8 });
  }, []);

  return (
    <div
      ref={mapExportRef}
      id={id}
      className={`ptel-map-viewer ${editMode.state.isActive ? 'ptel-map-viewer--editing' : ''} ${className}`}
      style={{ height, position: 'relative' }}
    >
      {/* Panel de filtros */}
      {showFilters && !editMode.state.isActive && (
        <FilterPanel
          data={data}
          filters={filters}
          onFiltersChange={setFilters}
          position={filterPosition}
          collapsible={filtersCollapsible}
          defaultCollapsed={filtersDefaultCollapsed}
          showStats={true}
          showSearch={true}
        />
      )}

      {/* ADV-02: Control de capas base */}
      {showLayerControl && (
        <div
          className={`ptel-map-viewer-layer-control ptel-map-viewer-layer-control--${layerControlPosition}`}
          style={{
            position: 'absolute',
            zIndex: 1000,
            ...(layerControlPosition.includes('top') ? { top: '10px' } : { bottom: '10px' }),
            ...(layerControlPosition.includes('right') ? { right: '10px' } : { left: '10px' }),
            backgroundColor: 'white',
            borderRadius: '4px',
            boxShadow: '0 1px 5px rgba(0,0,0,0.3)',
            padding: '8px',
          }}
        >
          <LayerControl
            activeProvider={activeProvider}
            onProviderChange={handleProviderChange}
            defaultExpanded={false}
            disabled={editMode.state.isActive}
          />
        </div>
      )}

      {/* V14-V16: Panel de edición */}
      {enableEditing && (
        <EditPanel
          state={editMode.state}
          onToggleEdit={handleToggleEdit}
          onConfirm={handleConfirmEdits}
          onDiscard={handleDiscardEdits}
          onUndoEdit={handleUndoEdit}
          onSelectEdit={handleSelectForEdit}
          position={editPanelPosition}
        />
      )}

      {/* Mapa con capa de infraestructuras */}
      <MapContainer
        ref={mapRef}
        center={center}
        zoom={initialZoom}
        height="100%"
        width="100%"
        onReady={handleMapReady}
        ariaLabel={ariaLabel}
        preferredProvider={activeProvider}
      >
        {/* F8.02: Controles interactivos */}
        <ZoomControl position="bottomright" showReset />
        <MunicipalitySearch position="topright" />

        {/* F7.03: Capa WMS Catastro (visible solo a zoom ≥ 14) */}
        <CatastroWMSLayer
          visible={showCatastroWMS}
          opacity={catastroWMSOpacity}
        />

        {/* VECMUN-c: término propio + vecinos con sus nombres. Siempre encendida, sin casilla.
            El apilado lo fijan los panes de la capa (390/395, bajo el 400 de los puntos). */}
        {terminoIne5 && <TerminoMunicipalCapa ine5={terminoIne5} />}

        {/* V17: Buscador de direcciones CartoCiudad */}
        {showAddressSearch && (
          <AddressSearch
            municipality={municipio}
            province={provincia}
            position={addressSearchPosition}
            onNavigate={handleAddressNavigate}
            showCoordinates={true}
            width={300}
          />
        )}

        {/* V14-V16: Usar capa editable o normal según modo */}
        {enableEditing ? (
          <EditableInfrastructureLayer
            data={filteredData}
            editMode={editMode.state.isActive}
            pendingEdits={editMode.state.pendingEdits}
            selectedEditId={editMode.state.selectedId}
            onDragEnd={handleDragEnd}
            onMapClick={handleMapClick}
            onSelectForEdit={(infra) => editMode.selectForEdit(infra.id)}
            enableClustering={shouldCluster}
            onInfrastructureClick={handleInfraSelect}
            showPopups={true}
            showTooltips={!editMode.state.isActive}
          />
        ) : (
          <>
            <InfrastructureLayer
              data={filteredData}
              enableClustering={shouldCluster}
              onInfrastructureClick={handleSelect}
              selectedId={selectedInfraId}
            />
            {/* A3x-4: captura de clic para click-to-set (solo si el consumidor lo cablea) */}
            {onLienzoClic && <LienzoClicCapture onClic={onLienzoClic} />}
            {onCursorMover && <CursorCoordCapture onMover={onCursorMover} />}
            {/* P2RED-S1-5b: capa efímera de candidatos de geocodificación asistida */}
            {candidatos != null && candidatos.length > 0 && (
              <CandidatosLayer candidatos={candidatos} onAplicar={onAplicarCandidato} />
            )}
          </>
        )}
      </MapContainer>

      {/* F8.06: Toolbar de exportación + leyenda */}
      {(showExport || showLegend) && (
        <div className="ptel-map-viewer-toolbar ptel-map-viewer-toolbar--bottomright">
          {showLegend && <MapLegend defaultExpanded={false} />}
          {showExport && (
            <MapExport
              mapContainerRef={mapExportRef}
              metadata={exportMetadata}
              filename={exportBaseFilename}
            />
          )}
        </div>
      )}

      {/* F8.25: Atribución de fuentes de datos */}
      {showAttribution && (
        <MapAttributionNotes position="bottom-left" defaultCollapsed={true} />
      )}

      {/* Indicador de datos vacíos */}
      {data.length === 0 && (
        <div className="ptel-map-viewer-empty">
          <p>No hay datos para mostrar</p>
        </div>
      )}

      {/* Indicador de filtros activos sin resultados */}
      {data.length > 0 && filteredData.length === 0 && (
        <div className="ptel-map-viewer-no-results">
          <p>No hay resultados con los filtros actuales</p>
        </div>
      )}

      {/* V14-V16: Indicador modo edición activo */}
      {editMode.state.isActive && (
        <div className="ptel-map-viewer-edit-indicator">
          <span className="ptel-map-viewer-edit-indicator__icon">✏️</span>
          <span className="ptel-map-viewer-edit-indicator__text">
            Modo edición activo
          </span>
          {editMode.state.selectedId && (
            <span className="ptel-map-viewer-edit-indicator__hint">
              Click en el mapa para posicionar
            </span>
          )}
        </div>
      )}

      {/* V14-V16: Diálogo de confirmación al salir */}
      {showExitConfirm && (
        <div className="ptel-map-viewer-confirm-overlay">
          <div className="ptel-map-viewer-confirm-dialog">
            <h3>¿Descartar cambios?</h3>
            <p>
              Tienes {editMode.state.pendingEdits.length} cambio
              {editMode.state.pendingEdits.length !== 1 ? 's' : ''} sin guardar.
            </p>
            <div className="ptel-map-viewer-confirm-actions">
              <button 
                className="ptel-btn ptel-btn--secondary"
                onClick={handleCancelExit}
              >
                Cancelar
              </button>
              <button 
                className="ptel-btn ptel-btn--danger"
                onClick={handleConfirmExit}
              >
                Descartar y salir
              </button>
              <button 
                className="ptel-btn ptel-btn--primary"
                onClick={() => {
                  handleConfirmEdits();
                  setShowExitConfirm(false);
                }}
              >
                Guardar y salir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default MapViewer;
